import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, Calendar } from 'lucide-react';
import Seo from '../components/Seo';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import JsonLd from '../components/JsonLd';
import {
  blogPosts,
  blogCategories,
  getReadingTime,
  getCategoryLabel,
  formatPostDate,
} from '../data/blogPosts';

const BlogPage = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('todas');

  const filteredPosts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return blogPosts
      .filter((p) => category === 'todas' || p.category === category)
      .filter(
        (p) =>
          !q ||
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q)
      )
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [search, category]);

  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Blog GENSOL - Renta de Generadores, Soldadoras y Torres de Iluminación',
    url: 'https://www.gensolver.com/blog',
    description:
      'Guías técnicas y consejos de expertos sobre renta de generadores de energía, soldadoras, torres de iluminación y mantenimiento industrial.',
    publisher: {
      '@type': 'Organization',
      name: 'GENSOL',
      url: 'https://www.gensolver.com',
    },
  };

  return (
    <>
      <Seo
        title="Blog de Renta de Generadores y Equipo Industrial | GENSOL"
        description="Guías técnicas sobre renta de generadores, soldadoras, torres de iluminación y mantenimiento industrial en Veracruz y el sureste de México."
        canonical="https://www.gensolver.com/blog"
      />

      <BreadcrumbSchema
        items={[
          { name: 'Inicio', url: 'https://www.gensolver.com/' },
          { name: 'Blog', url: 'https://www.gensolver.com/blog' },
        ]}
      />
      <JsonLd data={blogSchema} />

      <main data-page="blog" data-analytics-page="blog" className="pt-24">
        {/* Header */}
        <section className="bg-gray-900 text-white py-16">
          <div className="container mx-auto px-4">
            <p className="text-orange-500 font-semibold uppercase tracking-wider text-sm mb-3">
              Blog GENSOL
            </p>
            <h1
              data-testid="blog-h1"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold max-w-4xl leading-tight"
            >
              Blog de Renta de Generadores, Soldadoras y Torres de Iluminación
            </h1>
            <p className="text-gray-300 mt-4 max-w-2xl text-base md:text-lg">
              Guías técnicas, comparativas y consejos de mantenimiento escritos
              por especialistas con más de 25 años en la industria energética.
            </p>
          </div>
        </section>

        {/* Search + Categories */}
        <section className="bg-white border-b border-gray-200 sticky top-20 z-30">
          <div className="container mx-auto px-4 py-4 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2" data-testid="blog-categories">
              <button
                type="button"
                data-testid="blog-category-todas"
                onClick={() => setCategory('todas')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  category === 'todas'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-orange-100 hover:text-orange-600'
                }`}
              >
                Todas
              </button>
              {blogCategories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  data-testid={`blog-category-${cat.id}`}
                  onClick={() => setCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    category === cat.id
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-orange-100 hover:text-orange-600'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="search"
                data-testid="blog-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar artículos..."
                aria-label="Buscar artículos del blog"
                className="w-full pl-9 pr-4 py-2 rounded-full border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
        </section>

        {/* Posts grid */}
        <section className="py-14 bg-gray-100">
          <div className="container mx-auto px-4">
            {filteredPosts.length === 0 ? (
              <p data-testid="blog-no-results" className="text-gray-600 text-center py-16">
                No se encontraron artículos con esa búsqueda.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post) => (
                  <article
                    key={post.slug}
                    data-testid={`blog-card-${post.slug}`}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300 flex flex-col"
                  >
                    <Link to={`/blog/${post.slug}`} className="block overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.imageAlt}
                        loading="lazy"
                        width="600"
                        height="340"
                        className="w-full h-52 object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </Link>
                    <div className="p-6 flex flex-col flex-1">
                      <span className="inline-block self-start text-xs font-semibold uppercase tracking-wide text-orange-600 bg-orange-100 px-3 py-1 rounded-full mb-3">
                        {getCategoryLabel(post.category)}
                      </span>
                      <h2 className="text-lg font-bold text-gray-900 leading-snug mb-2">
                        <Link
                          to={`/blog/${post.slug}`}
                          data-testid={`blog-card-link-${post.slug}`}
                          className="hover:text-orange-500 transition-colors"
                        >
                          {post.title}
                        </Link>
                      </h2>
                      <p className="text-gray-600 text-sm mb-4 flex-1">{post.excerpt}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500 border-t border-gray-100 pt-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-orange-500" />
                          <time dateTime={post.date}>{formatPostDate(post.date)}</time>
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-orange-500" />
                          {getReadingTime(post)} min de lectura
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
};

export default BlogPage;
